<script lang="ts" setup>
import { Studio, StudioEvent, SearchParams } from '@/modules/Studios/types'
import { mdiChevronDown, mdiChevronUp } from '@mdi/js'
import { ref, watch, onMounted } from 'vue'
import StudioEventChip from '@/modules/Studios/components/tabs/StudioEventChip.vue'
import { fetchStudioCompetitions } from '@/enitites/studio/api/endpoints'
import Bugsnag from '@bugsnag/js'

const props = defineProps<{
  studioDetails: Studio | undefined
}>()

const competitions = ref<StudioEvent[]>([])
const loading = ref(false)
const sortBy = ref<'date' | 'title'>('date')
const sortDirection = ref<'asc' | 'desc'>('asc')
const pageNumber = ref(0)
const pageSize = ref(100) // Adjust as needed
const keyword = ref('')

const fetchCompetitions = async () => {
  if (!props.studioDetails?.id) return

  loading.value = true
  try {
    const params: SearchParams = {
      pageNumber: pageNumber.value,
      pageSize: pageSize.value,
      keyword: keyword.value,
      sortBy: sortBy.value === 'date' ? 'date' : 'name',
      sortDir: sortDirection.value,
    }

    const fetchedCompetitions = await fetchStudioCompetitions(props.studioDetails.id, params)
    competitions.value = fetchedCompetitions || []
  } catch (error) {
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
      event.addMetadata('studio', {
        context: 'Error fetching studio competitions',
        studioId: props.studioDetails?.id,
        searchParams: {
          pageNumber: pageNumber.value,
          pageSize: pageSize.value,
          keyword: keyword.value,
          sortBy: sortBy.value,
          sortDir: sortDirection.value,
        },
      })
    })
    competitions.value = []
  } finally {
    loading.value = false
  }
}

watch([sortBy, sortDirection], () => {
  fetchCompetitions()
})

watch(
  () => props.studioDetails?.id,
  () => {
    if (props.studioDetails?.id) {
      fetchCompetitions()
    }
  },
  { immediate: true }
)

onMounted(() => {
  if (props.studioDetails?.id) {
    fetchCompetitions()
  }
})
</script>

<template>
  <div
    v-if="loading"
    class="placeholder"
  >
    <v-progress-circular
      indeterminate
      color="primary"
    />
  </div>
  <div
    v-else-if="competitions.length"
    class="tab-content"
  >
    <div class="filter-row">
      <div class="sort">
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <div
              class="sort-string"
              v-on="on"
            >
              Sort by:
              <span v-if="sortBy === 'date'"> Date </span>

              <span v-else> A - Z </span>
            </div>
          </template>
          <v-list
            class="sort-menu"
            width="242"
          >
            <v-list-item>
              <span class="sort-title">Sort by</span>
            </v-list-item>

            <v-radio-group
              v-model="sortBy"
              class="mt-0"
            >
              <v-list-item
                class="d-flex justify-space-between"
                @click="sortBy = 'date'"
              >
                <span>Date created</span>
                <div>
                  <v-radio
                    class="mr-0"
                    value="date"
                  />
                </div>
              </v-list-item>

              <v-list-item
                class="d-flex justify-space-between"
                @click="sortBy = 'title'"
              >
                <span>A - Z</span>
                <div>
                  <v-radio
                    class="mr-0"
                    value="title"
                  />
                </div>
              </v-list-item>
            </v-radio-group>
          </v-list>
        </v-menu>
        <button
          class="sort-arrows"
          type="button"
          @click="sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'"
        >
          <v-icon class="icon-up">
            {{ mdiChevronUp }}
          </v-icon>
          <v-icon class="icon-down">
            {{ mdiChevronDown }}
          </v-icon>
        </button>
      </div>
    </div>
    <div class="events-list">
      <StudioEventChip
        v-for="event in competitions"
        :key="event.id"
        :event="event"
      />
    </div>
  </div>
  <div
    v-else
    class="placeholder"
  >
    No competitions found for this studio
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  font-weight: var(--font-weight-bold);
  font-size: 18px;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 20px;
}

.filter-row {
  display: flex;
  gap: 24px;
  justify-content: space-between;
  width: 100%;

  @media (max-width: $screen-sm) {
    flex-direction: column;
  }

  .sort {
    display: flex;
    align-items: center;
    margin-left: auto;
  }

  .sort-arrows {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.events-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
